import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  /**
   * False for a dialog that must be ended deliberately (a secret shown once): Escape and a backdrop
   * click are ignored, so a stray key cannot throw the only copy away.
   */
  dismissible?: boolean;
  size?: 'md' | 'lg';
}

/**
 * The one dialog. Hand-authored (no Radix — CLAUDE.md § Frontend), so it has to earn back what a library
 * would give: `role="dialog"` + `aria-modal`, a labelled title, focus moved in on open and restored on
 * close, Tab kept inside, Escape to close when allowed.
 */
export function Modal({ title, children, footer, onClose, dismissible = true, size = 'md' }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => previous?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible && onClose) {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismissible, onClose]);

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && dismissible) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        className={`modal modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="modal-header">
          <h2 id={titleId} className="t-heading" style={{ margin: 0 }}>{title}</h2>
          {dismissible && onClose && (
            <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
              ✕
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmDialogProps {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  pending?: boolean;
  /** Extra condition (e.g. a reason typed) before the confirm button enables. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** A deliberate second step before an irreversible or money-moving action. */
export function ConfirmDialog({
  title, children, confirmLabel, destructive = false, pending = false, confirmDisabled = false, onConfirm, onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      title={title}
      onClose={pending ? undefined : onCancel}
      dismissible={!pending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${destructive ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={pending || confirmDisabled}
          >
            {pending ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {children}
    </Modal>
  );
}

interface RevealOnceProps {
  title: string;
  /** The plaintext — held by the CALLER in local state and discarded on dismiss. */
  secret: string;
  /** What it is, for the warning: "API key", "import key", "founder password", "signing secret". */
  what: string;
  children?: ReactNode;
  onDismiss: () => void;
}

/**
 * Shows a secret exactly once (an API key, an import key, a webhook signing secret, a generated founder
 * password). The console never stores it — not in its database, not in `localStorage`, not in the query
 * cache — so closing this dialog is final. It cannot be closed by Escape or a stray click.
 */
export function RevealOnce({ title, secret, what, children, onDismiss }: RevealOnceProps) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied('copied');
    } catch {
      // Clipboard needs a secure context; the value stays selectable so it can be copied by hand.
      setCopied('failed');
    }
  }

  return (
    <Modal
      title={title}
      dismissible={false}
      footer={
        <button type="button" className="btn btn-primary" onClick={onDismiss}>
          I have stored it — close
        </button>
      }
    >
      <div className="callout callout-warning" role="alert">
        <span className="callout-icon" aria-hidden="true">!</span>
        <div className="t-body">
          <strong className="t-heading" style={{ display: 'block' }}>Shown once</strong>
          This {what} is displayed now and never again. The console does not keep a copy — store it
          before closing this window. Lost it? Issue a new one.
        </div>
      </div>
      <div className="secret-box">
        <code className="secret-value mono" data-testid="revealed-secret">{secret}</code>
        <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>
          {copied === 'copied' ? 'Copied' : 'Copy'}
        </button>
      </div>
      {copied === 'failed' && (
        <p className="t-caption muted" style={{ margin: 0 }}>
          The clipboard is not available here — select the value and copy it by hand.
        </p>
      )}
      {children}
    </Modal>
  );
}
